import { useState, useEffect, useRef } from "react";
import { Check, Zap } from "lucide-react";
import logo from "@/assets/new-logo-2.png";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useCreateCheckout, useSubscription } from "@/hooks/useSubscription";
import { useAccounts } from "@/hooks";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useIsMobile } from "@/hooks/useIsMobile";
import { toast } from "sonner";

type Tier = "duo" | "power";

const TIERS: Record<
  Tier,
  {
    name: string;
    tagline: string;
    monthly: string;
    annual: string;
    features: string[];
    popular?: boolean;
  }
> = {
  duo: {
    name: "Duo",
    tagline: "Personal + work, together at last",
    monthly: "€4/month",
    annual: "€39/year",
    features: [
      "2 Google accounts, one board",
      "Search & filter across accounts",
      "Drag & drop between lists",
      "No more account switching",
    ],
    popular: true,
  },
  power: {
    name: "Power",
    tagline: "For freelancers juggling client accounts",
    monthly: "€8/month",
    annual: "€76/year",
    features: [
      "Unlimited Google accounts",
      "Everything in Duo",
      "Built for client work",
      "Priority support",
    ],
  },
};

type UpgradeDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  source?: string;
};

const UpgradeDialog = ({ open, onOpenChange, source = "paywall" }: UpgradeDialogProps) => {
  const [billingCycle, setBillingCycle] = useState<"monthly" | "annual">("annual");
  const [tier, setTier] = useState<Tier>("duo");
  const { data: subscription } = useSubscription();
  const { data: accounts } = useAccounts();
  const createCheckout = useCreateCheckout();
  const analytics = useAnalytics();
  const isMobile = useIsMobile();
  const hasTrackedOpen = useRef(false);
  // Mobile checkout navigates in-place; keep the dialog up with feedback until
  // the browser actually leaves the page
  const [redirecting, setRedirecting] = useState(false);

  // Returning via the back button restores the page from bfcache with old JS
  // state — clear the redirecting flag so the dialog is usable again
  useEffect(() => {
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) setRedirecting(false);
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  useEffect(() => {
    if (open && !hasTrackedOpen.current) {
      hasTrackedOpen.current = true;
      const accountsConnected = accounts ? Object.keys(accounts).length : 0;
      analytics.track("paywall_viewed", {
        accounts_connected: accountsConnected,
        accounts_limit: subscription?.accountLimit ?? null,
        trial_active: subscription?.trialActive ?? false,
        source,
      });
    }
    if (!open) {
      hasTrackedOpen.current = false;
    }
  }, [open, accounts, analytics, source, subscription]);

  const variants = subscription?.variants;
  const variantId = variants
    ? tier === "duo"
      ? billingCycle === "monthly"
        ? variants.duoMonthly
        : variants.duoAnnual
      : billingCycle === "monthly"
        ? variants.powerMonthly
        : variants.powerAnnual
    : null;

  const handleUpgrade = async () => {
    if (!variantId) return;

    analytics.track("upgrade_clicked", { source, tier, billing_cycle: billingCycle });

    try {
      const redirectUrl = `${window.location.origin}/?checkout=success`;
      const url = await createCheckout.mutateAsync({ variantId, redirectUrl });

      analytics.track("checkout_started", { plan: `${tier}_${billingCycle}` });

      if (isMobile) {
        // window.open after an await is popup-blocked on iOS (the tap's
        // activation expires); same-tab navigation is always allowed and the
        // checkout redirects back to /?checkout=success. The dialog stays up
        // showing "Opening checkout" until the browser leaves the page.
        setRedirecting(true);
        window.location.assign(url);
      } else {
        onOpenChange(false);
        window.open(url, "_blank", "noopener,noreferrer");
      }
    } catch {
      toast.error("Something went wrong. Please try again.");
    }
  };

  const isLoading = createCheckout.isPending || redirecting || !variantId;
  const selected = TIERS[tier];
  const price = billingCycle === "monthly" ? selected.monthly : selected.annual;

  const trialDaysLeft =
    subscription?.trialActive && subscription.trialEndsAt
      ? Math.max(0, Math.ceil((new Date(subscription.trialEndsAt).getTime() - Date.now()) / 86400000))
      : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px] gap-5 overflow-hidden">
        <DialogHeader className="space-y-3">
          <div className="flex items-center gap-3">
            <img
              src={logo}
              alt="Task Fusion"
              className="h-9 w-9 rounded-lg object-contain"
              style={{ boxShadow: "0 0 12px rgba(37, 99, 235, 0.3)" }}
            />
            <div>
              <DialogTitle className="tracking-wide">Upgrade Task Fusion</DialogTitle>
              <DialogDescription className="mt-0.5">
                {trialDaysLeft != null
                  ? `Your free trial ends in ${trialDaysLeft} day${trialDaysLeft === 1 ? "" : "s"}. Keep all your accounts in one view.`
                  : "All your Google accounts, one view."}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="flex gap-1 rounded-lg bg-muted/60 p-1">
          <button
            className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-all ${
              billingCycle === "monthly"
                ? "bg-background shadow-sm text-foreground"
                : "text-muted-foreground hover:text-foreground"
            }`}
            onClick={() => setBillingCycle("monthly")}
          >
            Monthly
          </button>
          <button
            className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-all ${
              billingCycle === "annual"
                ? "bg-background shadow-sm text-foreground"
                : "text-muted-foreground hover:text-foreground"
            }`}
            onClick={() => setBillingCycle("annual")}
          >
            Annual
            <span className="ml-1.5 text-xs text-green-600 dark:text-green-400 font-semibold">Save 20%</span>
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          {(Object.keys(TIERS) as Tier[]).map((key) => {
            const t = TIERS[key];
            const isSelected = tier === key;
            return (
              <button
                key={key}
                onClick={() => setTier(key)}
                className={`relative rounded-lg px-4 py-3.5 text-left space-y-2.5 transition-all ${
                  isSelected ? "" : "opacity-70 hover:opacity-100"
                }`}
                style={{
                  background: isSelected ? "rgba(37, 99, 235, 0.06)" : "rgba(37, 99, 235, 0.02)",
                  border: isSelected
                    ? "1.5px solid rgba(37, 99, 235, 0.55)"
                    : "1px solid rgba(37, 99, 235, 0.1)",
                }}
              >
                {t.popular && (
                  <span
                    className="absolute -top-2.5 left-3 rounded-full px-2 py-0.5 text-[10px] font-semibold text-white"
                    style={{ background: "linear-gradient(135deg, #2563eb, #3b82f6)" }}
                  >
                    Most popular
                  </span>
                )}
                <div className="flex items-center gap-1.5">
                  {key === "power" && <Zap className="h-3.5 w-3.5 text-blue-500" />}
                  <span className="text-sm font-semibold">{t.name}</span>
                </div>
                <div className="text-lg font-bold">
                  {billingCycle === "monthly" ? t.monthly : t.annual}
                </div>
                <p className="text-xs text-muted-foreground">{t.tagline}</p>
                <div className="space-y-1.5 pt-1">
                  {t.features.map((feature) => (
                    <div key={feature} className="flex items-center gap-2">
                      <Check className="h-3 w-3 shrink-0 text-blue-500" strokeWidth={2.5} />
                      <span className="text-xs">{feature}</span>
                    </div>
                  ))}
                </div>
              </button>
            );
          })}
        </div>

        <button
          className="w-full py-2.5 text-sm font-medium rounded-lg text-white transition-all hover:brightness-110 active:scale-[0.99] disabled:opacity-50 disabled:pointer-events-none"
          onClick={handleUpgrade}
          disabled={isLoading}
          style={{
            background: isLoading
              ? "hsl(var(--muted-foreground))"
              : "linear-gradient(135deg, #2563eb, #3b82f6)",
            boxShadow: isLoading
              ? "none"
              : "0 2px 10px rgba(59, 130, 246, 0.35), 0 1px 3px rgba(0, 0, 0, 0.1)",
          }}
        >
          {redirecting ? "Opening checkout..." : isLoading ? "Loading..." : `Upgrade to ${selected.name} — ${price}`}
        </button>

        <p className="text-center text-xs text-muted-foreground/60">
          Cancel anytime. Your tasks always stay in your Google account.
        </p>
      </DialogContent>
    </Dialog>
  );
};

export default UpgradeDialog;
